/**
 ⚠️ Design System — Protected file
 Do not edit this file without prior approval from the Design System team.
 */

import { DefaultTheme } from 'styled-components/native'

import {
  BackgroundColor,
  BackgroundColorKey,
  BorderColor,
  ColorsType,
  IconColor,
  TextColor,
} from 'theme/types'

import { ButtonColor, ButtonColors, ButtonColorValue, ButtonVariant } from './types'
import {
  paletteKeysByColor,
  variantBaseKeys,
  variantDisabledKeys,
  variantHoverKeys,
} from './variants'

const getBackgroundColor = (
  background: BackgroundColor,
  key?: BackgroundColorKey | 'transparent'
): ButtonColorValue | undefined => {
  if (!key) return undefined
  if (key === 'transparent') return 'transparent'
  return background[key]
}

const getHoverIconColor = ({
  color,
  hoverTextColor,
  icon,
}: {
  color: ButtonColor
  hoverTextColor?: ColorsType
  icon: IconColor
}) => {
  if (!hoverTextColor) return undefined
  return color === 'brand' ? icon.brandPrimaryHover : (icon.brandPrimaryHover ?? hoverTextColor)
}

const getBaseKeys = ({
  variant,
  color,
  disabled,
}: {
  variant: ButtonVariant
  color: ButtonColor
  disabled: boolean
}) => {
  const baseKeys = variantBaseKeys[variant]
  const paletteKeys = paletteKeysByColor[color]
  const disabledKeys = disabled ? variantDisabledKeys[variant] : undefined

  return {
    backgroundKey: disabledKeys?.background ?? baseKeys.background,
    textKey: disabledKeys?.text ?? baseKeys.text ?? paletteKeys.text,
    iconKey: disabledKeys?.icon ?? baseKeys.icon ?? paletteKeys.icon,
    borderKey: disabledKeys?.border ?? baseKeys.border ?? paletteKeys.border,
    paletteKeys,
  }
}

const getBaseColors = ({
  background,
  border,
  icon,
  text,
  variant,
  color,
  disabled,
}: {
  background: BackgroundColor
  border: BorderColor
  icon: IconColor
  text: TextColor
  variant: ButtonVariant
  color: ButtonColor
  disabled: boolean
}) => {
  const { backgroundKey, textKey, iconKey, borderKey, paletteKeys } = getBaseKeys({
    variant,
    color,
    disabled,
  })

  const finalBackgroundKey =
    variant === 'primary' && color === 'neutral' ? 'inverted' : backgroundKey
  const finalBorderKey = variant === 'primary' && color === 'neutral' ? 'inverted' : borderKey

  const backgroundColor = getBackgroundColor(background, finalBackgroundKey)

  return {
    backgroundColor,
    textColor: text[textKey],
    iconColor: icon[iconKey],
    borderColor: variant === 'tertiary' ? undefined : border[finalBorderKey ?? paletteKeys.border],
  }
}

const getHoverColors = ({
  background,
  border,
  icon,
  text,
  variant,
  color,
  disabled,
}: {
  background: BackgroundColor
  border: BorderColor
  icon: IconColor
  text: TextColor
  variant: ButtonVariant
  color: ButtonColor
  disabled: boolean
}) => {
  if (disabled) {
    return {
      hoverTextColor: undefined,
      hoverIconColor: undefined,
      hoverBackgroundColor: undefined,
      hoverBorderColor: undefined,
    }
  }

  const hoverKeys = variantHoverKeys[variant]
  const hoverTextColor = hoverKeys?.text ? text[hoverKeys.text] : undefined

  return {
    hoverTextColor,
    hoverIconColor: getHoverIconColor({ color, hoverTextColor, icon }),
    hoverBackgroundColor: hoverKeys?.background ? background[hoverKeys.background] : undefined,
    hoverBorderColor:
      variant === 'tertiary' || !hoverKeys?.border ? undefined : border[hoverKeys.border],
  }
}

const applyTransparentSecondary = ({
  variant,
  transparent,
  disabled,
  backgroundColor,
  hoverBackgroundColor,
}: {
  variant: ButtonVariant
  transparent: boolean
  disabled: boolean
  backgroundColor?: ButtonColorValue
  hoverBackgroundColor?: ButtonColorValue
}) => {
  const isTransparentSecondary = variant === 'secondary' && transparent && !disabled

  return {
    backgroundColor: isTransparentSecondary ? 'transparent' : backgroundColor,
    hoverBackgroundColor: isTransparentSecondary ? 'transparent' : hoverBackgroundColor,
  }
}

export const getButtonColors = ({
  theme,
  variant,
  color,
  transparent,
  disabled,
}: {
  theme: DefaultTheme
  variant: ButtonVariant
  color: ButtonColor
  transparent: boolean
  disabled: boolean
}): ButtonColors => {
  const { background, border, icon, text } = theme.designSystem.color

  const baseColors = getBaseColors({
    background,
    border,
    icon,
    text,
    variant,
    color,
    disabled,
  })
  const hoverColors = getHoverColors({
    background,
    border,
    icon,
    text,
    variant,
    color,
    disabled,
  })
  const transparency = applyTransparentSecondary({
    variant,
    transparent,
    disabled,
    backgroundColor: baseColors.backgroundColor,
    hoverBackgroundColor: hoverColors.hoverBackgroundColor,
  })

  return {
    backgroundColor: transparency.backgroundColor ?? baseColors.backgroundColor,
    borderColor: baseColors.borderColor,
    textColor: baseColors.textColor,
    iconColor: baseColors.iconColor,
    hoverTextColor: hoverColors.hoverTextColor,
    hoverIconColor: hoverColors.hoverIconColor,
    hoverBackgroundColor: transparency.hoverBackgroundColor ?? hoverColors.hoverBackgroundColor,
    hoverBorderColor: hoverColors.hoverBorderColor,
  }
}
